import React, {useEffect, useImperativeHandle, useMemo, useRef, useState} from 'react';
import {Form, Input, Select, SelectOptionWithGroup, Table, Dropdown, List, notification, FormItemProps} from 'tea-component';
import { columnCheck } from '@dbl/services/dataSource';
import type { ITableColumnDto, ITableColumnUpdateDto } from '@dbl/services/dataSource';
import { FieldType } from '@dbl/core/dataSource';
import {
  DimensionType,
  DimensionValue,
  Include255,
} from '@dbl/core/dataSource';
import { DateTypeComponent } from '@/pages/datalab/components/SortableDrag/FieldEditComponents';
import {getLocalText} from "@/pages/datalab/utils/utils";

const { autotip } = Table.addons;

function CellInput(props: any) {
  const { onChange, isEn, records=[], ...resetProps} = props;
  const [validation, setValidation] = useState<{status: FormItemProps['status']; message: string; messageEn: string;}>({status: undefined, message: '', messageEn: ''});
  return <Form.Item
    style={{width: '100%', display: 'block'}}
    status={validation.status}
    message={validation[isEn ? 'messageEn' : 'message']}
    showStatusIcon={false}
  >
    <Input
      onChange={(value) => {
        const newValidation = {status: undefined, message: '', messageEn: ''};
  
        if (!newValidation.status) {
          const item = records.find((data) => (data?.display_name || data?.name) === value);
          
          if (item) {
            newValidation.status = 'error';
            newValidation.message = '重复命名';
            newValidation.messageEn = 'Duplicate naming';
          }
        }
        
        if (newValidation.status !== validation.status) {
          setValidation(newValidation);
        }
        onChange(value, newValidation);
      }}
      {...resetProps}
    />
  </Form.Item>;
}

function FieldTypeSelect(props: any) {
  const {defaultValue, column, columnName, tableId, formatMessage, onChange, isEn} = props;
  const [selected, setSelected] = useState(defaultValue);
	
  const [checkStatus, setCheckStatus] = useState<string|undefined>(undefined);
  const clickFun = (value: string, dateTypeValue?: string) => {
    if (selected !== value || dateTypeValue) {
      setSelected(value);
      onChange(value, dateTypeValue);
    }
  };
  return (<Dropdown
    boxClassName={'dbl-data-edit-list'}
    style={{width: isEn ? 125 : 110}}
    button={formatMessage(selected)}
    appearance={'button'}
    placement="bottom-start"
    placementOffset={[0, 4]}
    trigger={'click'}
    clickClose={false}
    flipped={false}
    boxAdaptable={true}
  >
    {
      (subClose) => (<List type="option" style={{maxHeight: 180}}>
        <List.Item
          selected={selected === FieldType.String}
          onClick={() => {
            subClose();
            clickFun(FieldType.String);
          }}
        >
          {formatMessage(FieldType.String)}
        </List.Item>
        <List.Item
          className={'submenu'}
          selected={selected === FieldType.Date}
          disabled={checkStatus === 'disabled'}
        >
          <DateTypeComponent
            formatMessage={formatMessage}
            checkStatus={checkStatus}
            column={column}
            onClick={(toDateFormat: any) => {
              setCheckStatus('loading');
              columnCheck({ table_id: tableId, column: columnName, to_date_format: toDateFormat }).then((result: any) => {
                // date_format返回空字符串或者没有得到date_format, 则视为日期格式校验失败, 无法转换成日期格式
                if (result?.date_format) {
                  setCheckStatus(result?.date_format || '');
                  subClose();
                  clickFun(FieldType.Date,toDateFormat);
                  notification.success({
                    description: formatMessage('check_date_success')
                  });
                } else {
                  // 无法转换为日期类型
                  notification.error({
                    description: formatMessage('check_date_fail')
                  });
                  // setOpen(false)
                  setCheckStatus('disabled');
                }

              }).catch(() => {
                // 校验请求发生错误, 无法进行校验, 下一次打开时还需要校验.
                setCheckStatus(undefined);
                // setOpen(false)
              });
            }}
          />
        </List.Item>
				
        <List.Item
          selected={selected === FieldType.Number}
          onClick={() => { subClose();clickFun(FieldType.Number)}}
        >
          {formatMessage(FieldType.Number)}
        </List.Item>
        <List.Item
          selected={selected === FieldType.Float}
          onClick={() => { subClose();clickFun(FieldType.Float)}}
        >
          {formatMessage(FieldType.Float)}
        </List.Item>
        <List.Item
          selected={selected === FieldType.Percent}
          onClick={() => { subClose();clickFun(FieldType.Percent)}}
        >
          {formatMessage(FieldType.Percent)}
        </List.Item>
      </List>)
    }
  </Dropdown>);
}

function FieldsTableForm(props: any, ref: any) {
  const { tableId, dataFormatMessage, commonFormatMessage, isEn, loading, error, dataList } = props;
  const [records, setRecords] = useState<ITableColumnDto[]>([]);
  useEffect(() => {
    setRecords(dataList ||[]);
  }, [dataList]);
  
  const newFieldRef: React.MutableRefObject<Record<string, Partial<ITableColumnUpdateDto>>> = useRef({});
  const validationRef = useRef({});
	
  useImperativeHandle(ref, () => ({
    getNewFields: (): ITableColumnUpdateDto[] => {
      const newFields: ITableColumnUpdateDto[] = [];
      Object.keys(newFieldRef.current)?.forEach(name => {
        const fieldInfo = newFieldRef.current[name] || {};
        if (JSON.stringify(fieldInfo) !== '{}')
          newFields.push({...fieldInfo, column_name: name});
      });
      return newFields;
    },
    getValidations: () => Object.keys(validationRef.current).map(fieldName => ({name: fieldName, ...validationRef.current[fieldName]}))
  }));
	
	
	
  const { columns } = useMemo(() => {
		
    // 选项
    const optionsOfCategory: SelectOptionWithGroup[] = [
      {
			  text: dataFormatMessage('dimension'),
        value: String(DimensionType.Normal)
      },
      {
        text: dataFormatMessage('metric'),
        value: String(DimensionType.Metric)
      },
    ];
    const optionsOfValue: SelectOptionWithGroup[] = [
      {
        text: dataFormatMessage('origin'),
        value: DimensionValue.Origin
      },
      {
        text: dataFormatMessage('system'),
        value: DimensionValue.System,
        disabled: true,
      }
    ];
    const optionsOfInclude: SelectOptionWithGroup[] = [
      {
        text: dataFormatMessage('include'),
        value: String(Include255.Include)
      },
      {
        text: dataFormatMessage('exclude'),
        value: String(Include255.Exclude)
      },
    ];
		
    const onFormItemChange = (value: string | number, oldValue: string | number, fieldName: string, columnKey: keyof ITableColumnUpdateDto): void => {
      if (value !== oldValue) {
        const newValue = newFieldRef.current[fieldName] || {};
        newValue[columnKey] = value;
        newFieldRef.current[fieldName] = newValue;
      } else {
        delete newFieldRef.current[fieldName]?.[columnKey];
      }
    };
		
    const columns = [
      {
        key: 'display_name',
        header: dataFormatMessage('display_name'), // 显示名称
        width: 130,
        render(record: ITableColumnDto, rowKey: string) {
          // TODO 写一个 公共的onChange方法, 传递 rowKey, 用于区分存值; 与旧值对比, 相等的情况下, 不存储或者删除已存储的, 用于用户是否有更改的判断.
          // 校验单独写
          const text = (isEn ? record.display_name_en : record.display_name) || record.display_name || record.name;
          return <CellInput key={isEn} isEn={isEn} size={'full'} records={records.filter((item: ITableColumnDto) =>(item.name !== record.name))} defaultValue={text} onChange={(value, validation) => {
            onFormItemChange(value, text, rowKey, 'display_name');
            validation.status === 'error' ? (validationRef.current[rowKey] = validation) : (delete validationRef.current[rowKey]);
          }}/>;
        }
      },
      {
        key: 'name',
        header: dataFormatMessage('field_name'), // 字段名称
        width: 120,
      },
      {
        key: 'type',
        header: dataFormatMessage('field_type'), // 字段类型
        width: isEn ? 150 : 140,
        render(record: ITableColumnDto, rowKey: string, recordIndex: number) {
          return (<FieldTypeSelect
            isEn={isEn}
            formatMessage={dataFormatMessage}
            defaultValue={record.type}
            onChange={(value: string, dateTypeValue: string) => {
              onFormItemChange(value, record.type, rowKey, 'column_type');
              if (value === FieldType.Date) {
                const updateValue = newFieldRef.current[rowKey] || {};
                const newRecord = { to_date_format: dateTypeValue || '' };
                setRecords((records) => {
                  Object.assign(records[recordIndex], newRecord);
                  return [...records];
                });
                updateValue.to_date_format = dateTypeValue;
                newFieldRef.current[rowKey] = updateValue;
              };
            }}
            columnName={record.name}
            column={record}
            tableId={tableId}
          />);
        }
      },
      {
        key: 'attribute',
        header: dataFormatMessage('metric_dimension'), // 维度/指标
        width: isEn ? 180 : 140,
        render(record: ITableColumnDto, rowKey: string, recordIndex: number) {
          // TODO 需求中已经没有公共维度的概念了, 但数据中还有
          // 后端返回的是公共维度类型, 但前端只有 维度和指标两种
          // 后端返回了 敏感字段(record.attribute是敏感字段) ,  前端如何处理
          // 需要和凡平对一下上面的问题
          const defaultValue = record.is_metric ? DimensionType.Metric : DimensionType.Normal; // is_metric不会变化, 可以作为原始值 和 新值进行判断
          return <Select
            style={{ width: isEn ? 120 : 80}}
            appearance="button"
            options={optionsOfCategory}
            defaultValue={String(defaultValue)}
            onChange={(value) => {
              const updateValue = newFieldRef.current[rowKey] || {};
              const newValue = Number(value);
              const newRecord = { attribute: newValue };
              if (newValue == DimensionType.Metric) {
                // 从维度转换到指标
                if (newValue === defaultValue) {
                  // 说明用户恢复成原来的值, 相当于没有变化
                  delete updateValue.attribute;
                  newRecord.dimension_setting = undefined;
                  newRecord.flag = undefined;
                } else {
                  updateValue.attribute = newValue;
                }
                delete updateValue.dimension_setting;
                delete updateValue.flag;
								
              } else {
                // 从指标转换到维度
                if (newValue === defaultValue) {
                  // 说明用户恢复成原来的值, 相当于没有变化
                  delete updateValue.attribute;
                } else {
                  updateValue.attribute = newValue;
                  updateValue.dimension_setting = record.dimension_setting || DimensionValue.Origin;
                  updateValue.flag = record.flag == undefined ? Include255.Exclude : record.flag;
                  newRecord.dimension_setting = updateValue.dimension_setting;
                  newRecord.flag = updateValue.flag;
                }
              }
              setRecords((records) => {
                Object.assign(records[recordIndex], newRecord);
                return [...records];
              });
              newFieldRef.current[rowKey] = updateValue;
            }}
          />;
        }
      },
      {
        key: 'dimension_setting',
        header: dataFormatMessage('dimension_value_settings'), // 维度值设置
        width: isEn ? 222 : 140,
        render(record: ITableColumnDto, rowKey: string) {
          return record.attribute === DimensionType.Metric ? '-' : (<Select
            overlayStyle={{width: isEn ? 170 : 120}}
            appearance="button"
            options={optionsOfValue}
            defaultValue={record.dimension_setting}
            onChange={(value) => {onFormItemChange(value, record.dimension_setting, rowKey, 'dimension_setting')}}
          />);
        }
      },
      {
        key: 'flag',
        header: dataFormatMessage('include_dimension_total_value'), // 是否含维度汇总
        width: isEn ? 256 : 160,
        render(record: ITableColumnDto, rowKey: string) {
          return record.attribute === DimensionType.Metric ? '-' : (<Select
            style={{ width: 100}}
            appearance="button"
            options={optionsOfInclude}
            defaultValue={String(record.flag)}
            onChange={(value) => {onFormItemChange(Number(value), record.flag, rowKey, 'flag')}}
          />);
        }
      },
      {
        key: 'remark',
        width: 240,
        header: dataFormatMessage('field_desc'), // 字段说明
        render(record: ITableColumnDto, rowKey: string) {
          const text = getLocalText(isEn, record.remark, record.remark_en);
          return <Input key={isEn} size={'full'} defaultValue={text} onChange={(value) => {
            onFormItemChange(value, text, rowKey, 'remark');
          }}/>;
        }
      },
    ];
		
    return { columns };
  }, [isEn, records]);
	
  return (<Table
    className={'dbl-table-edit-table'}
    recordKey={'name'}
    columns={columns}
    records={records}
    addons={[
      // scrollable({
      // 	maxHeight: 600,
      // 	scrollToTopOnChange: true,
      // }),
      autotip({
        isError: !!error,
        isLoading: loading,
        style: {lineHeight: '200px'},
        emptyText: commonFormatMessage("no_data"),
        loadingText: commonFormatMessage("loading"),
        errorText: commonFormatMessage("load_fail"),
      }),
    ]}
  />);
}

export default React.forwardRef(FieldsTableForm);